/**
 * Voir Dire tab — question bank rendering, filtering, and plain-text export.
 * Faithful port of the monolith's renderVD / setVDFilter / renderVDContent /
 * buildVDBankText. All question prose is preserved verbatim from VD_QUESTIONS.
 */

import type { VoirDireQuestion } from '@/types';
import { store } from '@/app/store';
import { byId, $$ } from '@/ui/dom';
import { CHARGES } from '@/data/charges';
import { VD_QUESTIONS } from '@/data/voir-dire';
import { getDetectedCharges, allChargeText } from '@/core/citations';

/** Detected charge slugs from the current case's charge description. */
function detectedCharges(): ReturnType<typeof getDetectedCharges> {
  return getDetectedCharges(allChargeText(store.getState().chargeList, store.getState().chargeDescription));
}

export function renderVD(): void {
  const detected = detectedCharges();
  const banner = byId('vd-charge-banner');
  if (banner) {
    const labelText = detected.length
      ? detected.map((k) => (CHARGES[k] ? CHARGES[k].label : k)).join(', ')
      : 'All charges';
    banner.textContent = 'Questions generated for: ' + labelText;
  }
  renderVDContent();
}

export function setVDFilter(f: string, el: HTMLElement): void {
  store.getState().vdFilter = f;
  store.save();
  $$('#page-vd .filter-chip').forEach((c) => {
    c.classList.remove('active');
  });
  el.classList.add('active');
  renderVDContent();
}

export function renderVDContent(): void {
  const detected = detectedCharges();
  const vdFilter = store.getState().vdFilter;

  const allQs = VD_QUESTIONS.filter((q) => {
    const cs = q.charges;
    const chargeMatch =
      cs === 'all' ||
      detected.length === 0 ||
      (Array.isArray(cs) && detected.some((k) => cs.indexOf(k) !== -1));
    const sideMatch =
      vdFilter === 'all' ||
      q.side === vdFilter ||
      (vdFilter === 'gen' && q.side === 'gen');
    return chargeMatch && sideMatch;
  });

  function buildQHtml(q: VoirDireQuestion): string {
    const tagCls =
      q.side === 'def' ? 'vd-tag-def' : q.side === 'pros' ? 'vd-tag-pros' : 'vd-tag-gen';
    const tagLbl =
      q.side === 'def' ? 'Defense' : q.side === 'pros' ? 'Prosecution' : 'General';
    let chargeTag = '';
    if (q.charges !== 'all') {
      const cKey = Array.isArray(q.charges) ? q.charges[0] : q.charges;
      chargeTag =
        '<span class="vd-tag vd-tag-gen">' +
        (CHARGES[cKey] ? CHARGES[cKey].label : cKey) +
        '</span>';
    }
    return (
      '<div class="vd-q" id="vdq-' +
      q.id +
      '" onclick="tapVDQ(' +
      q.id +
      ')">' +
      '<div class="vd-q-num">Q' +
      q.id +
      ' <span class="vd-tag ' +
      tagCls +
      '">' +
      tagLbl +
      '</span>' +
      chargeTag +
      '</div>' +
      q.text +
      '</div>'
    );
  }

  function buildSection(qs: VoirDireQuestion[]): string {
    const cats: Record<string, VoirDireQuestion[]> = {};
    qs.forEach((q) => {
      if (!cats[q.cat]) cats[q.cat] = [];
      cats[q.cat].push(q);
    });
    let h = '';
    Object.entries(cats).forEach((e) => {
      h += '<div class="card"><div class="vd-section-hdr">' + e[0] + '</div>';
      e[1].forEach((q) => {
        h += buildQHtml(q);
      });
      h += '</div>';
    });
    return h;
  }

  const genQs = allQs.filter((q) => q.charges === 'all');
  const specQs = allQs.filter((q) => q.charges !== 'all');

  let html = '';
  if (genQs.length) {
    html +=
      '<div style="font-size:12px;font-weight:700;color:var(--navy);text-transform:uppercase;letter-spacing:0.7px;padding:10px 4px 4px;">General Questions</div>';
    html += buildSection(genQs);
  }
  if (specQs.length) {
    const chargeLabel = detected.length
      ? detected.map((k) => (CHARGES[k] ? CHARGES[k].label : k)).join(', ')
      : 'Charge-Specific';
    html +=
      '<div style="font-size:12px;font-weight:700;color:var(--navy);text-transform:uppercase;letter-spacing:0.7px;padding:14px 4px 4px;">Charge-Specific Questions — ' +
      chargeLabel +
      '</div>';
    html += buildSection(specQs);
  }
  if (!html)
    html =
      '<div class="card"><div class="empty-state"><div class="empty-icon">❓</div><div class="empty-title">No questions match</div><div class="empty-sub">Try selecting a different filter</div></div></div>';

  const content = byId('vd-content');
  if (content) content.innerHTML = html;
}

// ─── VOIR DIRE BANK EXPORT HELPERS ───
export function buildVDBankText(): string {
  const state = store.getState();
  const detected = detectedCharges();
  const vdFilter = state.vdFilter;

  const qs = VD_QUESTIONS.filter((q) => {
    const cs = q.charges;
    const chargeMatch =
      cs === 'all' ||
      detected.length === 0 ||
      (Array.isArray(cs) && detected.some((k) => cs.indexOf(k) !== -1));
    const sideMatch =
      vdFilter === 'all' ||
      q.side === vdFilter ||
      (vdFilter === 'gen' && q.side === 'gen');
    return chargeMatch && sideMatch;
  });

  const cats: Record<string, VoirDireQuestion[]> = {};
  qs.forEach((q) => {
    if (!cats[q.cat]) cats[q.cat] = [];
    cats[q.cat].push(q);
  });

  const divider = '─'.repeat(60);
  const sideLabel =
    vdFilter === 'def'
      ? 'Defense'
      : vdFilter === 'pros'
        ? 'Prosecution'
        : vdFilter === 'gen'
          ? 'General'
          : 'All';
  const chargeLabel = detected.length
    ? detected.map((k) => (CHARGES[k] ? CHARGES[k].label : k)).join(', ')
    : 'All charges';

  let out =
    'COMMONWEALTH OF MASSACHUSETTS\n' +
    (state.court || '[COURT]').toUpperCase() +
    '\n\n' +
    'VOIR DIRE QUESTION BANK\n' +
    divider +
    '\n\n' +
    'Case: ' +
    (state.caseName || '[Case Name]') +
    '\n' +
    'Docket No.: ' +
    (state.docket || '[Docket No.]') +
    '\n' +
    'Next Event Date: ' +
    (state.trialDate || '[Next Event Date]') +
    '\n' +
    'Presiding: ' +
    (state.judge || '[Judge]') +
    '\n' +
    'Filter: ' +
    sideLabel +
    ' | Charges: ' +
    chargeLabel +
    '\n' +
    divider +
    '\n\n';

  Object.entries(cats).forEach((e) => {
    const cat = e[0];
    const catQs = e[1];
    out += cat.toUpperCase() + '\n' + '─'.repeat(40) + '\n\n';
    catQs.forEach((q, i) => {
      const sideTag =
        q.side === 'def' ? '[Defense]' : q.side === 'pros' ? '[Prosecution]' : '[General]';
      out += i + 1 + '.  ' + sideTag + '\n    ' + q.text + '\n\n';
    });
  });

  return out;
}
